import React, { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import MiniHeader from '../../../../components/layout/MiniHeader/index.js'
import Typography from '../../../../components/common/Typography/index'
import useAppSelector from '../../../../state/redux/hooks/index'
import { markAsRead } from '../../../../state/redux/actions/notificationActions.js'
import { fetchNotifications } from '../../../../state/redux/actions/notificationActions.js'
import ErrorState from '../../../../components/state/ErrorState.jsx'
import LoadingIndicator from '../../../../components/state/LoadingIndicator.jsx'
import Pagination from '../../../../components/common/Pagination/Pagination.jsx'
import Button from '../../../../components/common/Button/Button.jsx'
import NotificationForm from '../../../../components/forms/NotificationForm/NotificationForm.jsx'
import Backdrop from '../../../../components/common/Backdrop/Backdrop.jsx'
import { formatTimestamp } from '../../../../shared/utils/dateUtils'
import Modal from '../../../../components/layout/Modal/index'
import { BsThreeDotsVertical } from 'react-icons/bs'
import Tabs from '../../../../components/common/Tabs/Tabs.jsx'

const NotificationList = () => {
  const dispatch = useDispatch()
  const { notifications, loading, error, sendSuccess } = useAppSelector(state => state.notifications)
  const [selectedNotification, setSelectedNotification] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [activeTab, setActiveTab] = useState('all')
  const [formOpen, setFormOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  const [isSubmitting, setIsSubmitting] = useState(false) // Prevent multiple submissions
  const [readNotifications, setReadNotifications] = useState(new Set()) // Track locally read notifications
  const [dateFilter, setDateFilter] = useState(null)
  const [isFiltering, setIsFiltering] = useState(false)

  useEffect(() => {
    dispatch(fetchNotifications())
  }, [dispatch])

  // Auto-refresh notifications when a notification is sent successfully
  useEffect(() => {
    if (sendSuccess) {
      // Refresh the notifications list
      dispatch(fetchNotifications())
      // Close the form and reset submitting state
      setFormOpen(false)
      setIsSubmitting(false)
    }
  }, [sendSuccess, dispatch])

  const tabs = [
    { id: 'all', label: 'All' },
    { id: 'unread', label: 'Unread' },
    { id: 'read', label: 'Read' },
  ]
  const normalizedTab = activeTab?.toLowerCase()

  // Filter notifications by date range
  const filterNotificationsByDate = (notificationList, startDate, endDate) => {
    if (!startDate || !endDate || !notificationList || !Array.isArray(notificationList)) {
      return notificationList || []
    }

    return notificationList.filter(notification => {
      const notificationDate = new Date(notification.timestamp || notification.createdAt)
      const start = new Date(startDate)
      const end = new Date(endDate)

      // Set time boundaries
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)

      return notificationDate >= start && notificationDate <= end
    })
  }

  const filteredNotifications = useMemo(() => {
    // Apply local read state to notifications
    let notificationsWithLocalState = notifications.map(notification => ({
      ...notification,
      isRead: notification.isRead || readNotifications.has(notification._id || notification.id)
    }))

    // Apply date filter first if actively filtering
    if (isFiltering && dateFilter) {
      notificationsWithLocalState = filterNotificationsByDate(notificationsWithLocalState, dateFilter.startDate, dateFilter.endDate)
    }

    // Apply tab filter
    switch (normalizedTab) {
      case 'unread':
        return notificationsWithLocalState.filter(n => n.isRead === false)
      case 'read':
        return notificationsWithLocalState.filter(n => n.isRead === true)
      default:
        return notificationsWithLocalState
    }
  }, [notifications, normalizedTab, readNotifications, dateFilter, isFiltering])

  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filteredNotifications.slice(startIndex, endIndex)
  }, [currentPage, itemsPerPage, filteredNotifications])

  const handlePageChange = page => {
    const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage)
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    } else if (page > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    } else if (totalPages === 0) {
      setCurrentPage(1)
    }
  }

  const handleTabChange = tab => {
    setActiveTab(tab)
    setCurrentPage(1)
  }

  // Handle filter change from MiniHeader
  const handleMiniHeaderFilterChange = (filtered, dateRange) => {
    if (!dateRange) {
      // No date filter applied - reset to show all data
      setIsFiltering(false)
      setDateFilter(null)
      setCurrentPage(1)
      return
    }

    // Date filter is applied - set filtering state
    setIsFiltering(true)
    setDateFilter(dateRange)
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handleRefresh = () => {
    setCurrentPage(1)
    setReadNotifications(new Set()) // Clear local read state on refresh
    dispatch(fetchNotifications())
  }

  const handleNotificationClick = async (notification) => {
    setSelectedNotification(notification)
    setModalOpen(true)

    if (!notification.isRead && !readNotifications.has(notification._id || notification.id)) {
      const notificationId = notification._id || notification.id
      
      // Immediately mark as read locally for instant UI feedback
      setReadNotifications(prev => new Set([...prev, notificationId]))
      
      try {
        // Dispatch the action to mark as read on server
        await dispatch(markAsRead(notificationId))
      } catch (error) {
        // If marking as read fails, remove from local read state
        setReadNotifications(prev => {
          const newSet = new Set(prev)
          newSet.delete(notificationId)
          return newSet
        })
        console.error('Failed to mark notification as read:', error)
      }
    }
  }

  const handleSendNotificationClick = () => {
    if (!isSubmitting) {
      setIsSubmitting(true)
      setFormOpen(true)
    }
  }

  const handleFormClose = () => {
    setFormOpen(false)
    setIsSubmitting(false)
    // Refresh notifications when form is closed to ensure we have latest data
    dispatch(fetchNotifications())
  }

  console.log('selectedNotification', selectedNotification)
  console.log('All notifications:', notifications)
  console.log('Send success:', sendSuccess)

  return (
    <>
      <MiniHeader
        title="Notifications"
        data={notifications}
        onFilterChange={handleMiniHeaderFilterChange}
      />
      <div className="ml-[250px] flex-1 p-8 w-[1040px] mx-auto my-0">
        {loading && <LoadingIndicator />}
        {!loading && error && <ErrorState error={error} />}
        {!loading && !error && notifications.length === 0 && (
          <div>
            <Typography variant="body">No notifications found</Typography>
          </div>
        )}
        {!loading && !error && notifications.length > 0 && (
          <div className="">
            {/* header */}
            <div className="flex gap-6 justify-between">
              {/* tabs */}
              <Tabs tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} justify="center" />
              {/* pagination */}
              <div className="mt-2 flex gap-6">
                <div className="mt-4">
                  <Button 
                    size="small" 
                    onClick={handleSendNotificationClick}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Notification'}
                  </Button>
                </div>
                <Pagination
                  currentPage={currentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredNotifications.length}
                  onPageChange={handlePageChange}
                  onRefresh={handleRefresh}
                  justify="end"
                />
              </div>
            </div>
            {/* notification form */}
            {formOpen && (
              <Backdrop isOpen={formOpen} onClose={handleFormClose}>
                <NotificationForm 
                  error={error} 
                  checked={formOpen}
                  onToggle={() => {
                    setFormOpen(false)
                    setIsSubmitting(false)
                  }}
                />
              </Backdrop>
            )}

            {/* notification list */}
            {currentItems && currentItems.length > 0 && (
              <div className="p-4">
                {currentItems.map(notification => {
                  const isRead = notification.isRead || readNotifications.has(notification._id || notification.id)
                  
                  return (
                    <div
                      key={notification._id || notification.id}
                      className="bg-white rounded-lg shadow p-4 mb-4 hover:shadow-md transition-shadow cursor-pointer flex items-start"
                      onClick={() => handleNotificationClick(notification)}
                    >
                      {!isRead ? (
                        <div className="bg-primary rounded-full w-2 h-2 flex-shrink-0 mt-1 mr-4" />
                      ) : (
                        <div className="w-3 h-3 mr-4 mt-1 flex-shrink-0" />
                      )}
                      <div className="flex-1">
                        <div className="flex justify-between">
                          <Typography variant="h5" className="text-sm">{notification.type.replace(/_/g, ' ')}</Typography>

                          <Typography variant="h6" color="secondary">
                            {formatTimestamp(notification.timestamp || notification.createdAt)}
                          </Typography>
                        </div>

                        <Typography variant="h6" className="block font-semibold">
                          {notification.title}
                        </Typography>
                        
                        {/* Show a preview of the description */}
                        <Typography variant="body" color="secondary" className="mt-1 text-sm">
                          {notification.description?.length > 100 
                            ? `${notification.description.substring(0, 100)}...` 
                            : notification.description
                          }
                        </Typography>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            
            {/* Show message when filtered list is empty but main list has items */}
            {filteredNotifications.length === 0 && notifications.length > 0 && (
              <div className="p-4 text-center">
                <Typography variant="body">
                  {isFiltering
                    ? `No ${normalizedTab === 'all' ? '' : normalizedTab + ' '}notifications found for the selected date range`
                    : `No ${normalizedTab} notifications found`
                  }
                </Typography>
              </div>
            )}

            {/* Notification detail modal */}
            {modalOpen && selectedNotification && (
              <Backdrop isOpen={modalOpen} onClose={() => setModalOpen(false)}>
                <Modal
                  title={selectedNotification?.type?.replace(/_/g, ' ') || ''}
                  subtitle={selectedNotification ? formatTimestamp(selectedNotification.timestamp || selectedNotification.createdAt) : ''}
                  isOpen={modalOpen}
                  onClose={() => setModalOpen(false)}
                  icon={<BsThreeDotsVertical className="text-base mt-2 cursor-pointer" />}
                >
                  <Typography>
                    <strong>{selectedNotification.title}</strong>
                  </Typography>
                  <Typography>{selectedNotification.description}</Typography>
                  
                  {/* Show attachment if available */}
                  {selectedNotification.attachment && (
                    <div className="mt-4">
                      <Typography variant="body" className="mb-2">
                        <strong>Attachment:</strong>
                      </Typography>
                      <a 
                        href={selectedNotification.attachment} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        View Attachment
                      </a>
                    </div>
                  )}
                </Modal>
              </Backdrop>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export default NotificationList